import { DivConst } from 'constants/divConst';

import type { FormEditFormStateType } from 'types/page/formEditFormStateType';
import { isEmpty } from 'utils/app';

/**
 * HTML フィールドが実質空かどうかを判定する。
 * タグを除去したテキストが空の場合、または値が null / undefined / 空文字の場合に true を返す。
 */
export function isHtmlFieldEmpty(html: string | null | undefined): boolean {
  if (html === null || html === undefined || html === '') {
    return true;
  }
  // HTML タグを除去してテキスト部分だけ取り出す
  const text = html.replace(/<[^>]*>/g, '');
  return isEmpty(text);
}

/**
 * respondentFields の中に getFlag=true のフィールドが 1 件以上あるかを返す。
 */
export function hasAnyRespondentFieldGetFlag(values: FormEditFormStateType): boolean {
  return values.respondentFields.some(f => f.getFlag);
}

/**
 * lineUrl が必須入力かどうかを返す。
 * lineFlag が enable 値の場合に true。
 */
export function isLineUrlRequired(values: FormEditFormStateType): boolean {
  return Number(values.lineFlag) === DivConst.items.lineFlag.enable.value;
}

/**
 * 回答者情報の取得項目（respondentMailDiv / getFlag）の整合性を検証する。
 * 「取得項目なし」以外を選択しているのに取得項目チェックが 0 件の場合にエラーを返す。
 * エラーがない場合は null を返す。
 */
export function validateRespondentMailDivRequired(
  values: FormEditFormStateType
): { name: string; message: string } | null {
  const { formRespondentMail } = DivConst.items;
  if (
    Number(values.respondentMailDiv) !== formRespondentMail.none.value &&
    !isEmpty(values.respondentMailDiv) &&
    !hasAnyRespondentFieldGetFlag(values)
  ) {
    return {
      name: 'respondentMailDiv',
      message: '回答者情報の取得項目を 1 件以上選択してください',
    };
  }
  return null;
}

/**
 * 設問の選択肢数を検証し、上限超過があればエラー情報の配列を返す。
 * エラーがない場合は空配列を返す。
 */
export function validateQuestionOptionCount(
  question: FormEditFormStateType['questions'][0],
  questionIndex: number
): Array<{ name: string; message: string }> {
  const errors: Array<{ name: string; message: string }> = [];
  const qType = Number(question.questionType);
  const { questionType, questionOptionLimit } = DivConst.items;

  let limit: number | null = null;
  let label = '';

  if (qType === questionType.radio.value) {
    limit = questionOptionLimit.radio;
    label = 'ラジオボタン';
  } else if (qType === questionType.checkbox.value) {
    limit = questionOptionLimit.checkbox;
    label = 'チェックボックス';
  } else if (qType === questionType.image.value) {
    limit = questionOptionLimit.image;
    label = '画像のボタン';
  }

  if (limit !== null && question.options.length > limit) {
    errors.push({
      name: `questions.${questionIndex}.options`,
      message: `${label}の選択肢は${limit}件以内で入力してください`,
    });
  }

  return errors;
}

/**
 * フォーム全体の必須バリデーションを実行し、エラー情報の配列を返す。
 * エラーがない場合は空配列を返す。
 */
export function collectRequiredErrors(
  values: FormEditFormStateType
): Array<{ name: string; message: string }> {
  const errors: Array<{ name: string; message: string }> = [];

  // トップレベルの文字列フィールド
  if (isEmpty(values.privacyPolicyUrl)) {
    errors.push({ name: 'privacyPolicyUrl', message: 'プライバシーポリシーURLを入力してください' });
  }

  if (isEmpty(values.description)) {
    errors.push({ name: 'description', message: '説明文を入力してください' });
  }

  // HTML フィールド
  if (isHtmlFieldEmpty(values.reportMailSignatureHtml)) {
    errors.push({ name: 'reportMailSignatureHtml', message: 'メール署名を入力してください' });
  }

  // フォームテンプレート
  if (isEmpty(values.formTemplateId)) {
    errors.push({ name: 'formTemplateId', message: 'テンプレートを選択してください' });
  }

  // 回答者メール＆フィールド
  const respondentMailDivError = validateRespondentMailDivRequired(values);
  if (respondentMailDivError) {
    errors.push(respondentMailDivError);
  }

  // LINE URL
  if (isLineUrlRequired(values) && isEmpty(values.lineUrl)) {
    errors.push({ name: 'lineUrl', message: 'LINE URL を入力してください' });
  }

  // 設問
  const { questionType } = DivConst.items;
  const optionRequiredTypes = new Set([
    questionType.radio.value,
    questionType.checkbox.value,
    questionType.select.value,
    questionType.image.value,
  ]);

  values.questions.forEach((q, qi) => {
    if (isEmpty(q.title)) {
      errors.push({ name: `questions.${qi}.title`, message: '設問タイトルを入力してください' });
    }

    const qType = Number(q.questionType);

    // 選択肢タイトルの検証（radio / checkbox / select / image のみ）
    if (optionRequiredTypes.has(qType)) {
      q.options.forEach((opt, oi) => {
        if (isEmpty(opt.title)) {
          errors.push({
            name: `questions.${qi}.options.${oi}.title`,
            message: '選択肢タイトルを入力してください',
          });
        }
      });
    }

    // 選択肢数の上限検証
    const optionCountErrors = validateQuestionOptionCount(q, qi);
    errors.push(...optionCountErrors);
  });

  // 結果
  values.results.forEach((r, ri) => {
    if (isHtmlFieldEmpty(r.descriptionHtml)) {
      errors.push({
        name: `results.${ri}.descriptionHtml`,
        message: '結果説明を入力してください',
      });
    }
  });

  return errors;
}
